import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  ButtonMode,
  DocumentIcon,
  DownloadIcon,
  Popover,
  ButtonSize,
  NotificationType,
  Notification,
  LoadingSpinner
} from 'edenred-ui';
import { useTheme } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { DropdownOptionAPI } from '@ingestro/importer-react';

import { routerPaths } from '@epi-constants/routerPaths';
import { getTemplateUrl } from '@epi-pages/Beneficiaries/helpers/getTemplateUrl';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { FileFormatEnum } from '@epi-models/pages/Beneficiaries';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import {
  useCancelImportMutation,
  useGetImportStatusQuery
} from '@epi-api/massUpload/massUpload';
import { useGetDetailsQuery } from '@epi-api/user';

import { IngestroImporter, IngestroError } from './components';
import {
  FileFormatButton,
  ImportEmployeesContent,
  ImportEmployeesPanel,
  IngestroWrapper,
  SimpleLink
} from './ImportEmployees.styles';
import { useImportEmployees } from './useImportEmployees';
import { useLeaveImportEmployeesConfirmation } from './useLeaveImportEmployeesConfirmation';
import { AnotherUploadInProgress } from './components/AnotherUploadInProgress';
import { LeaveImportEmployeesModal } from './components/LeaveImportEmployeesModal';

export const ImportEmployees = () => {
  const { primaryFrontColor } = useTheme();
  const language = useSelector(getApplicationLanguageId);
  const { data: groupSettings } = useGetBenefitGroupsQuery();
  const [cancelImport] = useCancelImportMutation();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [ingestroError, setIngestroError] = useState<Error | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const { data: importStatus } = useGetImportStatusQuery();
  const { data: customerData, isLoading: isLoadingCustomerData } =
    useGetDetailsQuery();
  const hasEmployees = customerData?.isEmployeesSet;
  useImportEmployees({
    ingestroError,
    setIngestroError
  });
  const isAnotherUpload =
    importStatus?.isAnyImportInProgress && !isLoadingCustomerData;

  const handleLeave = useCallback(() => {
    if (uploadInProgress && requestId) {
      cancelImport({ requestId });
      setUploadInProgress(false);
    }
  }, [uploadInProgress, requestId, cancelImport]);

  const shouldConfirmLeave = !(
    importStatus?.isAnyImportInProgress && !isLoadingCustomerData
  );

  const { isOpen, confirmLeave, cancelLeave, allowNextNavigation } =
    useLeaveImportEmployeesConfirmation(shouldConfirmLeave, handleLeave);

  const groups = groupSettings?.map(group => ({
    label: group.groupName,
    value: group.groupName,
    alternativeMatches: [],
    type: 'string' as const
  })) as DropdownOptionAPI[];

  const isPopoverOpen = Boolean(anchorEl);

  const openDropdown = event => {
    setAnchorEl(event.currentTarget);
  };
  const closeDropdown = () => setAnchorEl(null);

  return (
    <ImportEmployeesPanel
      hasOverflow={false}
      title={t('containers.beneficiaries.import_employees.title')}
      backIcon
      backIconLink={routerPaths.beneficiaries}
      headerContent={
        ingestroError || isAnotherUpload ? null : (
          <Button
            id="download-template-button"
            mode={ButtonMode.Secondary}
            size={ButtonSize.Medium}
            onClick={openDropdown}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <DownloadIcon />
              {t('containers.beneficiaries.import_employees.download_button')}
            </Box>
          </Button>
        )
      }
      footer={
        uploadInProgress && requestId
          ? {
              hideSubmitButton: true,
              backText: t(
                'containers.beneficiaries.import_employees.in_progress.cancel'
              ),
              onBack: () => {
                cancelImport({ requestId });
                setUploadInProgress(false);
              }
            }
          : undefined
      }
    >
      {ingestroError && <IngestroError />}
      {isLoadingCustomerData && <LoadingSpinner />}
      {isAnotherUpload ? (
        <AnotherUploadInProgress hasEmployees={hasEmployees} />
      ) : (
        <ImportEmployeesContent
          display={
            ingestroError || importStatus?.isAnyImportInProgress
              ? 'none'
              : 'flex'
          }
        >
          <Box pb={6}>
            <Notification
              id="import-employees-info"
              type={NotificationType.Info}
              text={
                <Trans
                  i18nKey={
                    uploadInProgress
                      ? t(
                          'containers.beneficiaries.import_employees.in_progress.notification'
                        )
                      : t('containers.beneficiaries.import_employees.info')
                  }
                  components={{
                    a: (
                      <SimpleLink
                        target="_blank"
                        rel="noreferrer"
                        href={t(
                          'containers.beneficiaries.import_employees.info_url'
                        )}
                      />
                    )
                  }}
                />
              }
            />
          </Box>
          <Popover
            open={isPopoverOpen}
            anchorEl={anchorEl}
            onClose={closeDropdown}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            disableAutoFocus
          >
            <Box display="flex" flexDirection="column">
              <FileFormatButton
                id="file-format-button-xlxs"
                href={getTemplateUrl(language, FileFormatEnum.XLSX)}
              >
                <Box display="flex" gap={1.5} py={1} px={2}>
                  <DocumentIcon color={primaryFrontColor} />
                  {t(
                    'containers.beneficiaries.import_employees.file_format.xlxs'
                  )}
                </Box>
              </FileFormatButton>
              <FileFormatButton
                id="file-format-button-csv"
                href={getTemplateUrl(language, FileFormatEnum.CSV)}
              >
                <Box display="flex" gap={1.5} py={1} px={2}>
                  <DocumentIcon color={primaryFrontColor} />
                  {t(
                    'containers.beneficiaries.import_employees.file_format.csv'
                  )}
                </Box>
              </FileFormatButton>
            </Box>
          </Popover>
          <IngestroWrapper className="import-employees-wrapper">
            {groups ? (
              <IngestroImporter
                groups={groups}
                uploadInProgress={uploadInProgress}
                setUploadInProgress={setUploadInProgress}
                requestId={requestId}
                setRequestId={setRequestId}
                allowNextNavigation={allowNextNavigation}
              />
            ) : (
              <LoadingSpinner />
            )}
          </IngestroWrapper>
        </ImportEmployeesContent>
      )}
      <LeaveImportEmployeesModal
        isOpen={isOpen}
        onStay={cancelLeave}
        onLeave={confirmLeave}
      />
    </ImportEmployeesPanel>
  );
};
